import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getUnusedCampaignCodes, getUsedCampaignCodes } from "../../../actions/makeAWish";
import DualListBox from "../../listbox/DualListBox";
import defaultLang from "../../listbox/lang/default";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Loading from "../../Loading";
import { IAppState } from "../../../types/stores";

type Props = {
    id: string;
    onChange: (newCodes: Array<string>, hasError?: boolean) => void;
};

const CampaignCodes: React.FC<Props> = ({ id, onChange }: Props) => {
    const dispatch = useDispatch();

    const unusedCampaignCodes = useSelector((state: IAppState) => state.makeAWish.unusedCampaignCodes || []);
    const usedCampaignCodes = useSelector((state: IAppState) => state.makeAWish.usedCampaignCodes || []);
    const mailOnlySuppress = useSelector((state: IAppState) => state.makeAWish.mailOnlySuppress || "");
    const isCodesLoading = useSelector((state: IAppState) => state.makeAWish.isCodesLoading);

    const [usedCodes, setUsedCodes] = useState<Array<string>>([]);
    const [options, setOptions] = useState<Array<{ label: string; value: string; tooltip: string }>>([]);
    const [mos, setMailOnlySuppress] = useState("");
    const [error, setError] = useState((usedCodes.length > 0 && mos == "") || (usedCodes.length == 0 && mos != ""));

    let lang = defaultLang;
    lang.availableHeader = "Available";
    lang.selectedHeader = "Selected";

    useEffect(() => {
        // get the columns of campaign codes
        dispatch(getUnusedCampaignCodes(id)); // left
        dispatch(getUsedCampaignCodes(id)); // right
    }, [id, dispatch]);

    useEffect(() => {
        //create the full list of options
        const codes = unusedCampaignCodes.map(x => ({
            label: x.FullCode,
            value: x.Code,
            tooltip: x.FullCode,
        }));

        usedCampaignCodes.forEach(x =>
            codes.push({
                label: x.FullCode,
                value: x.Code,
                tooltip: x.FullCode,
            })
        );

        setOptions(codes);
        setUsedCodes(usedCampaignCodes.map(x => x.Code));
    }, [usedCampaignCodes, unusedCampaignCodes]);

    useEffect(() => {
        setMailOnlySuppress(mailOnlySuppress);
    }, [mailOnlySuppress]);

    const onListChange = newUsedCodes => {
        const hasError = (newUsedCodes.length > 0 && mos == "") || (newUsedCodes.length == 0 && mos != "");
        setUsedCodes(newUsedCodes);
        setError(hasError);
        onChange(newUsedCodes, hasError);
    };

    const handleChange = e => {
        const hasError =
            (usedCodes.length > 0 && e.target.value == "") || (usedCodes.length == 0 && e.target.value != "");
        setMailOnlySuppress(e.target.value || "");
        setError(hasError);
        onChange(usedCodes, hasError);
    };

    return (
        <div className="col">
            <div className="row m-t">
                <FormControl style={{ minWidth: 120 }} error={error}>
                    <InputLabel id="mail-only-suppress-label">Mail Only/Suppress</InputLabel>
                    <Select
                        labelId="mail-only-suppress-label"
                        id="mail-only-suppress-select"
                        value={mos}
                        label="Mail Only/Suppress"
                        onChange={handleChange}
                    >
                        <MenuItem value={""}>{"Select"}</MenuItem>
                        <MenuItem value={"M"}>{"Mail Only"}</MenuItem>
                    </Select>
                    {error && <FormHelperText>Mail Only must be selected when campaign codes are used.</FormHelperText>}
                </FormControl>
            </div>
            <div className="row m-t">
                <div className="col-sm-16 text-nowrap" style={{ height: "300px" }}>
                    {isCodesLoading ? (
                        <Loading />
                    ) : options.length > 0 ? (
                        <DualListBox
                            options={options}
                            canFilter
                            selected={usedCodes}
                            onChange={e => onListChange(e)}
                            showHeaderLabels={true}
                            lang={lang}
                        />
                    ) : (
                        <p>{"No Campaign Codes to Display."}</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CampaignCodes;
